import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Edit2Icon,
  Trash2Icon,
  SearchIcon,
  ContactIcon,
  MailIcon,
  UserPlusIcon,
  MoreVerticalIcon,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

// Type definition for Address

type Address = { 
  name: string
  email: string
  id?: string
  initials?: string
}

// Updated AddressBook component with compact rows, side-by-side actions, and bottom close

export const AddressBook = ({
  addresses,
  onUpdate,
  onClose,
}: {
  addresses: Address[]
  onUpdate: (addresses: Address[]) => void
  onClose: () => void
}) => {
  const [local, setLocal] = useState<Address[]>(addresses)
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [form, setForm] = useState<Address>({ name: '', email: '' })
  const [adding, setAdding] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const addressesWithInitials = addresses.map(addr => ({
      ...addr,
      initials: getInitials(addr.name),
      id: addr.id || generateId()
    }))
    setLocal(addressesWithInitials)
  }, [addresses])

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  const generateId = (): string => {
    return Math.random().toString(36).substr(2, 9)
  }

  const filteredContacts = local.filter(contact => 
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const startEdit = (idx: number) => {
    setEditingIdx(idx)
    setForm(local[idx])
  }
  const cancelEdit = () => {
    setEditingIdx(null)
    setForm({ name: '', email: '' })
  }
  const saveEdit = () => {
    const updated = local.map((a, i) => (i === editingIdx ? {
      ...form,
      initials: getInitials(form.name),
      id: a.id
    } : a))
    setLocal(updated)
    onUpdate(updated)
    cancelEdit()
  }
  const startAdd = () => {
    setForm({ name: '', email: '' })
    setAdding(true)
    setEditingIdx(null)
  }
  const cancelAdd = () => {
    setAdding(false)
    setForm({ name: '', email: '' })
  }
  const saveAdd = () => {
    const newContact = {
      ...form,
      initials: getInitials(form.name),
      id: generateId()
    }
    const updated = [...local, newContact]
    setLocal(updated)
    onUpdate(updated)
    cancelAdd()
  }
  const deleteItem = (idx: number) => {
    const updated = local.filter((_, i) => i !== idx)
    setLocal(updated)
    onUpdate(updated)
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="outline-none max-w-none"
        overlayClassName="bg-black/50 backdrop-blur-sm"
        onInteractOutside={(event) => event.preventDefault()}
      >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] flex flex-col border">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-100 rounded-lg">
              <ContactIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Contacts</h1>
              <p className="text-xs text-gray-500">{local.length} contact{local.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={startAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2"
            >
              <UserPlusIcon className="h-3 w-3 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b bg-gray-50/50">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white text-sm"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {adding && (
            <div className="p-4 border-b bg-blue-50/30">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-gray-900">Add Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm">Full Name</Label>
                    <Input
                      id="name"
                      placeholder="Enter name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="bg-white"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button 
                      size="sm"
                      onClick={saveAdd}
                      disabled={!form.name.trim() || !form.email.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Add
                    </Button>
                    <Button variant="outline" size="sm" onClick={cancelAdd}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {filteredContacts.length === 0 && !adding ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="p-3 bg-gray-100 rounded-full mb-3">
                <ContactIcon className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">
                {searchQuery ? 'No contacts found' : 'No contacts'}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                {searchQuery 
                  ? `No matches for "${searchQuery}"` 
                  : 'Add your first contact to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={startAdd} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <UserPlusIcon className="h-3 w-3 mr-1" />
                  Add Contact
                </Button>
              )}
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {filteredContacts.map((addr, idx) => (
                <Card key={addr.id || idx} className="transition-all duration-200 hover:shadow-sm">
                  <CardContent className="p-3">
                    {editingIdx === idx ? (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor={`edit-name-${idx}`} className="text-sm">Full Name</Label>
                          <Input
                            id={`edit-name-${idx}`}
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            className="bg-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`edit-email-${idx}`} className="text-sm">Email</Label>
                          <Input
                            id={`edit-email-${idx}`}
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            className="bg-white"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button 
                            size="sm"
                            onClick={saveEdit}
                            disabled={!form.name.trim() || !form.email.trim()}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Save
                          </Button>
                          <Button variant="outline" size="sm" onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Avatar className="h-10 w-10 bg-gradient-to-br from-blue-400 to-blue-600 flex-shrink-0">
                            <AvatarFallback className="text-white font-semibold text-sm">
                              {addr.initials || getInitials(addr.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 text-sm truncate">{addr.name}</h3>
                            <div className="flex items-center gap-1 text-gray-600">
                              <MailIcon className="h-3 w-3 flex-shrink-0" />
                              <span className="text-xs truncate">{addr.email}</span>
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600 h-8 w-8 p-0 flex-shrink-0">
                              <MoreVerticalIcon className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => startEdit(idx)}>
                              <Edit2Icon className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => deleteItem(idx)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2Icon className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      </DialogContent>
    </Dialog>
  )
}
